/**
 * Auth context provider — manages JWT tokens and user state.
 *
 * How it works:
 * - Access and CSRF tokens stay only in process memory
 * - Refresh session is an HttpOnly API cookie and rehydrates a page reload
 * - Provides login(), register(), logout(), and isAuthenticated
 * - The axios interceptor in client.ts handles token attachment + 401 refresh
 *
 * Why React Context (not Redux/Zustand)?
 *   Auth state is simple (user + loading flag). The rest of the app uses
 *   React Query for server state, so adding a state library just for auth
 *   would be over-engineering.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  loginUser,
  ensureDeviceBinding,
  registerUser,
  getMe,
  getAccessToken,
  getAuthCredentialEpoch,
  getAuthMemoryEpoch,
  getCsrfToken,
  isRefreshCookieQuarantined,
  setTokens,
  clearTokens,
  logoutUser,
  quarantineRefreshCookie,
  releaseRefreshCookieQuarantine,
  rebindCurrentRefreshBinding,
  refreshTokens,
  subscribeToAuthSync,
  type TokenResponse,
} from "../api/client";
import { identify, reset as resetAnalytics } from "../utils/analytics";
import i18n from "../i18n";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  oauth_provider: string | null;
  default_role: string | null;
  default_experience_level: string | null;
  plan_tier: string;
  evaluations_this_month: number;
  is_moderator: boolean;
  is_coach: boolean;
  is_admin: boolean;
  /** UI/AI language preference; backend resolves user.locale -> Accept-Language -> "en". */
  locale?: string;
}

export interface ExternalAuthCompletion {
  /** Pass this to the OAuth-code or verification redemption request. */
  signal: AbortSignal;
  /**
   * Atomically accepts a redeemed external-login response only if this flow is
   * still current. The callback is invoked synchronously only after /auth/me
   * has hydrated the accepted identity while the same epoch is still current.
   */
  accept: (
    tokens: TokenResponse,
    onAuthenticated: () => void,
  ) => Promise<boolean>;
  /**
   * Finalizes an OAuth/verification request failure through the same fence as
   * success. The page may alter history, errors, or navigation only when
   * `current` is true. Aborted superseded requests are intentionally inert
   * after cookie containment.
   */
  finalizeFailure: (
    requestMayHaveReachedServer?: boolean,
  ) => Promise<{ current: boolean; safeToPresent: boolean }>;
  /** Invalidates a LoginPage effect that unmounts before redemption completes. */
  cancel: () => void;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, acceptPolicies?: boolean) => Promise<void>;
  register: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<{ emailVerificationRequired: boolean }>;
  logout: () => Promise<void>;
  /**
   * Starts an OAuth-code/email-verification completion flow. LoginPage never
   * writes browser credentials directly; it redeems through this fenced handle.
   */
  startExternalAuthCompletion: () => ExternalAuthCompletion;
  /** Re-fetch user from the current in-memory token or HttpOnly cookie. */
  hydrate: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true); // true until initial hydration completes
  const authEpochRef = useRef(0);
  const authAbortRef = useRef<AbortController | null>(null);
  const externalFlowNonceRef = useRef(0);

  // Sets the user AND identifies them to analytics (id + plan_tier only —
  // never email/PII). Use this everywhere getMe() resolves a user.
  const applyUser = useCallback((u: AuthUser) => {
    setUser(u);
    identify(u.id, { plan_tier: u.plan_tier });
    // The signed-in account is the source of truth for UI language. i18next
    // otherwise floats on its localStorage/navigator detector, so without this
    // a user whose account is `en` can see a stale Spanish UI (and vice versa).
    // The switcher still lets anyone change it — it PATCHes user.locale, so the
    // account stays authoritative on the next hydrate.
    if (u.locale && u.locale !== i18n.language) {
      void i18n.changeLanguage(u.locale);
    }
  }, []);

  type AuthWork = {
    epoch: number;
    memoryEpoch: number;
    controller: AbortController;
  };

  const beginAuthWork = useCallback((
    dropCredentials: boolean,
    kind: "bootstrap" | "login" | "logout" | "hydrate" | "external",
  ): AuthWork => {
    // Keep the explicit call-site label as audit documentation for all
    // operations that supersede an external completion.
    void kind;
    authAbortRef.current?.abort();
    // Every login, logout, hydrate, or remote bootstrap invalidates a pending
    // OAuth/email completion handle before it can write returned credentials.
    externalFlowNonceRef.current += 1;
    if (dropCredentials) clearTokens(false);
    const controller = new AbortController();
    const work = {
      epoch: authEpochRef.current + 1,
      memoryEpoch: getAuthMemoryEpoch(),
      controller,
    };
    authEpochRef.current = work.epoch;
    authAbortRef.current = controller;
    return work;
  }, []);

  const isCurrentWork = useCallback((work: AuthWork): boolean => (
    authEpochRef.current === work.epoch
    && !work.controller.signal.aborted
    && getAuthMemoryEpoch() === work.memoryEpoch
  ), []);

  const rebindQuarantinedRefreshCookie = useCallback(async (
    expectedEpoch = getAuthMemoryEpoch(),
    signal?: AbortSignal,
  ): Promise<boolean> => {
    // A normal direct sign-in has no quarantine and its own Set-Cookie is
    // already authoritative. Quarantine is the only signal that an external
    // response could have changed cookie ordering after this bearer was issued.
    if (!isRefreshCookieQuarantined()) return true;
    const expectedCredentialEpoch = getAuthCredentialEpoch();
    if (!getAccessToken() || !getCsrfToken()) return false;
    try {
      const rebound = await rebindCurrentRefreshBinding(
        expectedEpoch,
        signal,
        expectedCredentialEpoch,
      );
      // The endpoint uses the captured bearer + CSRF and deliberately does
      // not read the browser cookie. Only its successful response authorizes
      // updating memory and releasing the shared-cookie bootstrap fence.
      setTokens(
        rebound.access_token,
        rebound.csrf_token,
        true,
        expectedEpoch,
      );
      return releaseRefreshCookieQuarantine(expectedEpoch);
    } catch {
      // Fail closed. Never bootstrap, refresh, or log out based on the
      // potentially cross-account cookie a stale completion installed.
      return false;
    }
  }, []);

  const containStaleExternalCookie = useCallback(async () => {
    // A late OAuth/verification response may already have updated the HttpOnly
    // cookie before JavaScript notices its flow is stale. Fence ordinary
    // cookie use before any await; containment never calls /auth/logout or
    // otherwise reads/revokes this unknown cookie.
    const bindingEpoch = getAuthMemoryEpoch();
    if (!quarantineRefreshCookie(bindingEpoch)) return;

    // If G2 has already bound a known bearer, overwrite the cookie via the
    // access-authenticated rebind endpoint. If G2 is still in flight (or no
    // bearer is known), remain quarantined; G2's token-binding path will make
    // the repair, and no-bearer tabs require an explicit fresh login.
    if (getAccessToken() && getCsrfToken()) {
      await rebindQuarantinedRefreshCookie(bindingEpoch);
    }
  }, [rebindQuarantinedRefreshCookie]);

  const startExternalAuthCompletion = useCallback((): ExternalAuthCompletion => {
    // Capture this work before redeeming the URL code/token. Any subsequent
    // manual login/logout/account switch invokes beginAuthWork and therefore
    // changes both the flow nonce and auth epoch.
    const work = beginAuthWork(true, "external");
    const flowNonce = externalFlowNonceRef.current;
    let settled = false;
    let failureFinalization:
      | Promise<{ current: boolean; safeToPresent: boolean }>
      | null = null;
    setLoading(true);
    setUser(null);
    resetAnalytics();

    const isCurrentExternalFlow = () => (
      !settled
      && externalFlowNonceRef.current === flowNonce
      && isCurrentWork(work)
    );
    // Failure containment may intentionally clear the flow's own credential
    // epoch. This narrower authority check distinguishes that self-contained
    // cleanup from a newer G2 work item, which advances both auth epoch and
    // external nonce.
    const stillOwnsExternalFlow = () => (
      !settled
      && externalFlowNonceRef.current === flowNonce
      && authEpochRef.current === work.epoch
      && !work.controller.signal.aborted
    );

    const finalizeFailure = (
      requestMayHaveReachedServer = true,
    ): Promise<{ current: boolean; safeToPresent: boolean }> => {
      // accept() invokes this before rethrowing its own failures; LoginPage
      // invokes it again from catch only to learn whether it is safe to render.
      // Returning the same promise makes that path idempotent.
      if (failureFinalization) return failureFinalization;

      const wasCurrent = isCurrentExternalFlow();
      failureFinalization = (async () => {
        // An abort does not establish that the browser cancelled the HTTP
        // request before the server or Set-Cookie. Contain stale flows and any
        // request that could have reached the server before UI-side effects.
        if (!wasCurrent || requestMayHaveReachedServer) {
          await containStaleExternalCookie();
        }
        let current = wasCurrent && stillOwnsExternalFlow();
        if (current) {
          // This failure is authoritative, but it must never leave a
          // half-accepted external response available to a later bootstrap.
          // Quarantine it and clear this flow's memory only; never call logout
          // against an unknown cookie because it may now belong to G2.
          const cleanupEpoch = getAuthMemoryEpoch();
          quarantineRefreshCookie(cleanupEpoch);
          clearTokens(false);
          setUser(null);
          setLoading(false);
          resetAnalytics();
          current = stillOwnsExternalFlow();
          if (current) settled = true;
        }
        return { current, safeToPresent: current };
      })();
      return failureFinalization;
    };

    return {
      signal: work.controller.signal,
      accept: async (tokens, onAuthenticated) => {
        if (!isCurrentExternalFlow()) {
          await containStaleExternalCookie();
          return false;
        }
        try {
          setTokens(
            tokens.access_token,
            tokens.csrf_token,
            true,
            work.memoryEpoch,
          );
          // A current external response is the only direct token writer, but
          // its Set-Cookie can still race a prior stale response. If containment
          // installed quarantine, repair it with the bearer-authenticated route
          // that ignores the unknown cookie before presenting the account.
          const bindingStillMatches = await rebindQuarantinedRefreshCookie(
            work.memoryEpoch,
            work.controller.signal,
          );
          if (!bindingStillMatches) {
            throw new Error("External authentication refresh binding changed.");
          }
          const me = await getMe(work.controller.signal);
          if (!isCurrentExternalFlow()) {
            await containStaleExternalCookie();
            return false;
          }
          applyUser(me);
          setLoading(false);
          settled = true;
          // Navigation/cache invalidation is supplied by LoginPage, but it is
          // invoked only while this exact completion remains authoritative.
          onAuthenticated();
          return true;
        } catch (error) {
          await finalizeFailure(true);
          throw error;
        }
      },
      finalizeFailure,
      cancel: () => {
        if (settled || externalFlowNonceRef.current !== flowNonce) return;
        settled = true;
        if (isCurrentWork(work)) {
          work.controller.abort();
          externalFlowNonceRef.current += 1;
          clearTokens(false);
          setUser(null);
          setLoading(false);
          resetAnalytics();
        }
        // Effect cleanup cannot await this, but it must still fence and revoke
        // a response that reached the server after AbortController fired.
        void containStaleExternalCookie();
      },
    };
  }, [
    applyUser,
    beginAuthWork,
    containStaleExternalCookie,
    isCurrentWork,
  ]);

  const bootstrapFromCookie = useCallback(async () => {
    // A URL credential hand-off (OAuth `?code=` or email `?verify_token=`) is
    // the authoritative session source for THIS load, and LoginPage's
    // redemption effect already owns it via startExternalAuthCompletion.
    // Effects flush child-before-parent, so that external completion is
    // registered in authAbortRef before this provider-level mount effect runs;
    // calling beginAuthWork here would abort its signal, and the verify/
    // exchange request — which is passed that signal — would silently never
    // leave the browser (bootstrap fires because it ignores the signal; verify
    // does not). Defer to the redemption instead of clobbering it. The guard is
    // on the URL, not on effect ordering, so it holds regardless of scheduling;
    // the redemption flow owns `loading` from here (it setLoading(true) on
    // start and settles it on accept/finalizeFailure).
    const handoff = new URLSearchParams(window.location.search);
    if (handoff.get("code") || handoff.get("verify_token")) {
      return;
    }
    // A reload or remote BroadcastChannel message has no authoritative account
    // identity. Clear memory first, then resolve the state from the HttpOnly
    // cookie endpoints. This also invalidates all earlier async results.
    const work = beginAuthWork(true, "bootstrap");
    setLoading(true);
    setUser(null);
    resetAnalytics();
    try {
      await refreshTokens({
        broadcast: false,
        expectedEpoch: work.memoryEpoch,
        signal: work.controller.signal,
      });
      if (!isCurrentWork(work)) return;
      const me = await getMe(work.controller.signal);
      if (!isCurrentWork(work)) return;
      applyUser(me);
    } catch {
      if (!isCurrentWork(work)) return;
      clearTokens(false);
      setUser(null);
      setLoading(false);
    } finally {
      if (isCurrentWork(work)) setLoading(false);
    }
  }, [applyUser, beginAuthWork, isCurrentWork]);

  // A page reload has no browser-readable credential to inspect. Bootstrap
  // through the HttpOnly refresh cookie instead of consulting storage.
  useEffect(() => {
    void bootstrapFromCookie();
    return () => authAbortRef.current?.abort();
  }, [bootstrapFromCookie]);

  // Broadcast messages are credential-free hints only. Both a remote login and
  // a remote logout can change the shared cookie jar, so resolve either one
  // from first-party cookie endpoints rather than trusting message content.
  useEffect(() => subscribeToAuthSync(() => {
    void bootstrapFromCookie();
  }), [bootstrapFromCookie]);

  const login = useCallback(async (
    email: string,
    password: string,
    acceptPolicies = false,
  ) => {
    const work = beginAuthWork(true, "login");
    setLoading(true);
    setUser(null);
    resetAnalytics();
    try {
      await ensureDeviceBinding(work.controller.signal);
      if (!isCurrentWork(work)) return;
      const tokens = await loginUser({
        email,
        password,
        ...(acceptPolicies ? { accept_terms: true, accept_privacy: true } : {}),
      });
      if (!isCurrentWork(work)) return;
      setTokens(
        tokens.access_token,
        tokens.csrf_token,
        true,
        work.memoryEpoch,
      );
      // A stale external completion can arrive while this password login was
      // in flight. If it quarantined the shared cookie, replace that cookie
      // from this access-authenticated G2 state before /me or any refresh path
      // can observe it.
      if (!await rebindQuarantinedRefreshCookie(
        work.memoryEpoch,
        work.controller.signal,
      )) {
        throw new Error("Unable to restore the authenticated refresh session.");
      }
      const me = await getMe(work.controller.signal);
      if (!isCurrentWork(work)) return;
      applyUser(me);
    } catch (error) {
      if (isCurrentWork(work)) {
        clearTokens(false);
        setUser(null);
        setLoading(false);
      }
      throw error;
    } finally {
      if (isCurrentWork(work)) setLoading(false);
    }
  }, [
    applyUser,
    beginAuthWork,
    isCurrentWork,
    rebindQuarantinedRefreshCookie,
  ]);

  const register = useCallback(
    async (email: string, password: string, displayName?: string) => {
      const result = await registerUser({
        email,
        password,
        display_name: displayName,
        accept_terms: true,
        accept_privacy: true,
      });
      return { emailVerificationRequired: result.email_verification_required };
    },
    []
  );

  const logout = useCallback(async () => {
    const csrfForLogout = getCsrfToken();
    const work = beginAuthWork(true, "logout");
    setUser(null);
    setLoading(false);
    resetAnalytics();
    try {
      await logoutUser(
        csrfForLogout,
        work.controller.signal,
        work.memoryEpoch,
      );
    } finally {
      // A new login/account switch may have started while the old logout was
      // in flight. It owns state and must not receive this late clear/broadcast.
      if (isCurrentWork(work)) clearTokens(true);
    }
  }, [beginAuthWork, isCurrentWork]);

  const hydrate = useCallback(() => {
    const work = beginAuthWork(false, "hydrate");
    setLoading(true);
    const load = async () => {
      try {
        if (getAccessToken()) {
          const me = await getMe(work.controller.signal);
          if (!isCurrentWork(work)) return;
          applyUser(me);
        } else {
          await refreshTokens({
            broadcast: false,
            expectedEpoch: work.memoryEpoch,
            signal: work.controller.signal,
          });
          if (!isCurrentWork(work)) return;
          const me = await getMe(work.controller.signal);
          if (!isCurrentWork(work)) return;
          applyUser(me);
        }
      } catch {
        if (!isCurrentWork(work)) return;
        clearTokens(false);
        setUser(null);
        setLoading(false);
      } finally {
        if (isCurrentWork(work)) setLoading(false);
      }
    };
    void load();
  }, [applyUser, beginAuthWork, isCurrentWork]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      startExternalAuthCompletion,
      hydrate,
    }),
    [
      user,
      loading,
      login,
      register,
      logout,
      startExternalAuthCompletion,
      hydrate,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
